import React, { useRef, useState, useEffect, forwardRef, useCallback, useMemo, } from 'react';
import { View, TextInput, TouchableOpacity, InteractionManager, } from 'react-native';
import _ from 'lodash';
import Animated, { withTiming, useSharedValue, useDerivedValue, useAnimatedStyle, Easing, } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import useColors from 'hooks/useColors';
import useFocusEffect from 'hooks/useFocusEffect';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { isWeb } from '../constants';
import IconClose from './icons/IconClose';
import IconSearch from './icons/IconSearch';
const HIT_SLOP = { left: 10, right: 10, top: 10, bottom: 10 };
const strings = {
    search: 'Search...',
    cancel: 'Cancel',
};
// On web, whether a blur event moves focus into the element behind ref
const isFocusMovingInto = (ref, event) => {
    const nextFocus = event
        ?.relatedTarget;
    const node = ref.current;
    return !!nextFocus && !!node?.contains?.(nextFocus);
};
export const SearchBar = forwardRef(({ onChangeSearchTerm, noDebounce, forSearchHeader, placeholder = 'Search', onFocus: onFocusFromProps, onBlur: onBlurFromProps, onCancel: onCancelFromProps, alwaysShowCancel, textFieldBackgroundColor, textColor, iconColor, textInputBgStyles, focusOnScreenFocus = true, containerStyle, autoCapitalize, debounceTime = 300, }, ref) => {
    const searchBarRef = useRef(null);
    const clearButtonRef = useRef(null);
    const cancelShownProgress = useSharedValue(0);
    const derivedCancelProgressValue = useDerivedValue(() => withTiming(cancelShownProgress.value, {
        duration: 150,
        easing: Easing.ease,
    }), []);
    const [searchTerm, setSearchTerm] = useState('');
    const focus = useCallback(() => {
        searchBarRef.current?.focus();
    }, []);
    useFocusEffect(useCallback(() => {
        if (searchTerm && focusOnScreenFocus)
            focus();
    }, [focus, focusOnScreenFocus, searchTerm]));
    const blur = useCallback(() => {
        searchBarRef.current?.blur();
    }, []);
    const debouncedRef = useRef(undefined);
    const debouncedOnChangeText = useMemo(() => {
        debouncedRef.current?.cancel();
        const debounced = _.debounce(onChangeSearchTerm, debounceTime);
        debouncedRef.current = debounced;
        return debounced;
    }, [onChangeSearchTerm, debounceTime]);
    useEffect(() => () => {
        debouncedRef.current?.cancel();
    }, []);
    const handleTextChange = (newText) => {
        setSearchTerm(newText);
        if (noDebounce) {
            onChangeSearchTerm(newText);
        }
        else {
            debouncedOnChangeText(newText);
        }
    };
    const setCancel = useCallback((value) => {
        cancelShownProgress.value = value;
    }, [cancelShownProgress]);
    const onFocus = useCallback(() => {
        onFocusFromProps?.();
        InteractionManager.runAfterInteractions(() => {
            setCancel(1);
        });
    }, [onFocusFromProps, setCancel]);
    const endSearchFocus = useCallback(() => {
        onBlurFromProps?.();
        if (!alwaysShowCancel && cancelShownProgress.value !== 0) {
            setCancel(0);
        }
    }, [alwaysShowCancel, cancelShownProgress, onBlurFromProps, setCancel]);
    const onBlur = useCallback((event) => {
        // On web, clicking or tabbing to the clear button moves focus to it. The search
        // is still in use, so that blur is skipped until focus leaves the button too.
        if (isWeb && isFocusMovingInto(clearButtonRef, event))
            return;
        endSearchFocus();
    }, [endSearchFocus]);
    const onClearButtonBlur = useCallback((event) => {
        if (!isFocusMovingInto(searchBarRef, event))
            endSearchFocus();
    }, [endSearchFocus]);
    const onClearSearch = useCallback(() => {
        setSearchTerm('');
        onChangeSearchTerm('');
        if (isWeb)
            focus();
    }, [focus, onChangeSearchTerm]);
    useEffect(() => {
        if (typeof ref === 'function') {
            ref({ focus, blur, clear: onClearSearch });
        }
        else if (ref) {
            // eslint-disable-next-line no-param-reassign
            ref.current = { focus, blur, clear: onClearSearch };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blur, focus, onClearSearch]);
    const timeoutRef = useRef(undefined);
    const onCancel = useCallback(() => {
        setSearchTerm('');
        if (alwaysShowCancel) {
            blur();
            onChangeSearchTerm('');
            onCancelFromProps?.();
            return;
        }
        setCancel(0);
        timeoutRef.current = setTimeout(() => {
            blur();
            onChangeSearchTerm('');
            onCancelFromProps?.();
        }, 250);
    }, [
        alwaysShowCancel,
        blur,
        onCancelFromProps,
        onChangeSearchTerm,
        setCancel,
    ]);
    useEffect(() => () => {
        clearTimeout(timeoutRef.current);
    }, []);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const textInputStyles = useMemo(() => [styles.searchInput, textColor ? { color: textColor } : undefined], [styles.searchInput, textColor]);
    const textInputBg = useMemo(() => [
        styles.searchBar,
        textInputBgStyles || {
            backgroundColor: textFieldBackgroundColor ?? colors.cards.onLight,
        },
    ], [
        colors.cards.onLight,
        styles.searchBar,
        textFieldBackgroundColor,
        textInputBgStyles,
    ]);
    const containerStyles = useMemo(() => {
        const style = {
            marginHorizontal: PADDING_HORIZONTAL,
            paddingVertical: SEARCH_BAR_MARGIN_VERTICAL,
            overflow: 'hidden',
        };
        if (forSearchHeader)
            style.paddingTop = SEARCH_BAR_CROPPED_PADDING_TOP;
        return [style, containerStyle];
    }, [containerStyle, forSearchHeader]);
    const contentAnimatedStyle = useAnimatedStyle(() => ({
        marginRight: CANCEL_BUTTON_WIDTH * (derivedCancelProgressValue.value - 1),
    }));
    return (<View style={containerStyles}>
        <Animated.View style={[styles.content, contentAnimatedStyle]}>
          <View style={styles.flex}>
            <Animated.View style={textInputBg}>
              <View style={styles.searchIconContainer}>
                <IconSearch color={iconColor}/>
              </View>
              <TextInput onBlur={onBlur} autoFocus={false} onFocus={onFocus} spellCheck={false} autoCapitalize={autoCapitalize} ref={searchBarRef} value={searchTerm} autoCorrect={false} autoComplete="off" placeholder={placeholder} style={textInputStyles} onChangeText={handleTextChange} selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder}/>
              {!!searchTerm && (<TouchableOpacity ref={clearButtonRef} onBlur={isWeb ? onClearButtonBlur : undefined} onPress={onClearSearch} style={styles.closeIcon}>
                  <IconClose color={iconColor || colors.text.secondary}/>
                </TouchableOpacity>)}
            </Animated.View>
          </View>
          <View style={styles.cancelButton}>
            <TouchableOpacity onPress={onCancel} hitSlop={HIT_SLOP}>
              <Text Text-14 color={textColor || colors.text.secondary}>
                {strings.cancel}
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>);
});
const BORDER_RADIUS = (() => rem(12))();
export const SEARCH_BAR_HEIGHT = (() => rem(48))();
export const SEARCH_BAR_HEIGHT_RAW = 48;
const PADDING_HORIZONTAL = (() => rem(16))();
const CLEAR_TOUCH_ZONE_WIDTH = (() => rem(56))();
export const SEARCH_BAR_MARGIN_VERTICAL = (() => rem(12))();
const SEARCH_BAR_CROPPED_PADDING_TOP = rem(8);
const CANCEL_BUTTON_WIDTH = rem(60);
const styleSet = createStyleSheets((colors) => ({
    content: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    searchBar: {
        flexDirection: 'row',
        alignItems: 'center',
        height: SEARCH_BAR_HEIGHT,
        borderRadius: BORDER_RADIUS,
    },
    searchInput: {
        flex: 1,
        padding: 0,
        fontSize: rem(16),
        color: colors.text.primary,
        fontFamily: 'Montserrat-Light',
        ...(isWeb && {
            caretColor: colors.text.selection,
        }),
    },
    closeIcon: {
        width: CLEAR_TOUCH_ZONE_WIDTH,
        height: SEARCH_BAR_HEIGHT,
        borderTopRightRadius: BORDER_RADIUS,
        borderBottomRightRadius: BORDER_RADIUS,
        alignItems: 'center',
        justifyContent: 'center',
    },
    flex: { flex: 1 },
    searchIconContainer: {
        paddingLeft: rem(16),
        paddingRight: rem(8),
    },
    cancelButton: {
        width: CANCEL_BUTTON_WIDTH,
        alignItems: 'flex-end',
    },
}));
export default React.memo(SearchBar);
